import React from "react";
import cn from "classnames";

import BackArrow from "../Icons/BackArrow";
import Search from "../Icons/Search";
import CloseIcon from "../Icons/CloseIcon";
import IconCompany from "../Icons/IconCompany";

import {
    DropdownProvider,
    DropdownProviderProps,
    DropdownClasses,
    DropdownContainerInputLabel,
    DropdownInputContainer,
    DropdownInput,
    DropdownClearInputButton,
    DropdownMenuContainer,
    DropdownMenuItems,
    DropdownMenuNoResults,
    DropdownMenuLinkItem
} from "./ComposableDropdown";

import styles from "./SearchDropdown.module.scss";

interface ComposableHeaderDropdownClasses {
    container?: string;
    containerOpened?: string;
    inputIcon?: string;
    postLabel?: string;
}

interface ComposableHeaderDropdownProviderProps extends DropdownProviderProps {
    /**
     * Component used as icon in input field
     */
    IconComponent?: React.ReactNode;
    /**
     * Text of label linked to input field
     */
    labelText?: string;
    /**
     * Message to appear if no results are found for input value in list, defaults to "No Results Found"
     */
    noResultsMessage?: string | React.ReactNode;
    dropdownTitle?: string;
 }

interface ComposableHeaderDropdownProps {
    classes?: DropdownClasses & ComposableHeaderDropdownClasses;
    dropdownProps: ComposableHeaderDropdownProviderProps;
    showRebrand?: boolean;
}

const ComposableHeaderDropdown = (props: ComposableHeaderDropdownProps) => {
    const { classes, dropdownProps } = props;
    const {
        labelText,
        inputType,
        noResultsMessage,
        IconComponent,
        onMenuToggle,
        inputId,
        ...restDropdownProps
    } = dropdownProps;
    const [isOpen, setIsOpen] = React.useState(false);

    const containerClassName = isOpen
        ? cn(styles.containerOpened, classes?.containerOpened)
        : cn(styles.container, classes?.container);

    const inputContainerClassName = isOpen
        ? cn(styles.inputContainerOpened, classes?.inputContainerOpened)
        : cn(styles.inputContainer, classes?.inputContainer);

    const menuClassName = isOpen
        ? cn(styles.menuOpened, classes?.menuOpened)
        : cn(styles.menu, classes?.menu);

    const dropdownClasses: DropdownClasses = {
        emptyResults: cn(styles.emptyResults, classes?.emptyResults),
        inputContainer: inputContainerClassName,
        inputLabel: cn(styles.inputLabel, classes?.inputLabel),
        input: cn(styles.input, classes?.input),
        inputClearButton: cn(styles.clearButton, classes?.inputClearButton),
        menu: menuClassName,
        menuList: cn(styles.menuList, classes?.menuList),
        menuContainer: cn(styles.menuContainer, classes?.menuContainer),
        listItem: cn(styles.listItem, classes?.listItem),
        listItemSelected: cn(styles.listItemSelected, classes?.listItemSelected),
        listItemText: cn(styles.listItemText, classes?.listItemText),
    };

    const menuToggle = (value: boolean) => {
        setIsOpen(value);
        onMenuToggle?.(value);
    };

    const iconComponent = IconComponent && React.isValidElement(IconComponent)
        ? React.cloneElement(IconComponent, { className: cn(styles.inputIcon, classes?.inputIcon) })
        : <Search className={cn(styles.inputIcon, classes?.inputIcon)} />;

    const baseProps: DropdownProviderProps = {
        classes: dropdownClasses,
        inputId,
        inputType,
        placeHolderText: "Search jobs or companies",
        onMenuToggle: menuToggle,
        closeMenuIfInputEmpty: true,
        showMenuOnFocus: false,
        refocusMobileOnClear: true,
        ...restDropdownProps
    };

    const CompanyPostLabel = () => <span className={cn(styles.postLabel, classes?.postLabel)}>Company</span>;
    const JobPostLabel = () => <span className={cn(styles.postLabel, classes?.postLabel)}>Jobs</span>;

    return (
        <div className={containerClassName}>
            {isOpen ? (
                <button type="button" className={styles.closeButton}>
                    <BackArrow />
                </button>
            ) : null}
            <DropdownProvider {...baseProps}>
                <DropdownContainerInputLabel>
                    {labelText}
                </DropdownContainerInputLabel>
                <DropdownInputContainer>
                    {iconComponent || null }
                    <DropdownInput />
                    <DropdownClearInputButton>
                        <CloseIcon />
                    </DropdownClearInputButton>
                </DropdownInputContainer>
                <DropdownMenuContainer>
                    <DropdownMenuItems>
                        <DropdownMenuLinkItem
                            prefixIcon={(index: number) => (index > 0 ? <IconCompany /> : <Search />)}
                            postLabelText={(index: number) => (index > 0 ? <CompanyPostLabel /> : <JobPostLabel />)}
                            openInNewWindow={true}
                        />
                        <DropdownMenuNoResults>
                            {noResultsMessage}
                        </DropdownMenuNoResults>
                    </DropdownMenuItems>
                </DropdownMenuContainer>
            </DropdownProvider>
        </div>
    );
};

export { ComposableHeaderDropdown as default };
export type { ComposableHeaderDropdownProps, ComposableHeaderDropdownProviderProps };
